import { Icon } from "@constellation-academy/academies-lib-webapp";
import { IconMap } from "@constellation-academy/epic-ui";
import { useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { match } from "ts-pattern";
import { stripHtmlFromTextParts } from "@components/cloze-text-learn-element/cloze-test-learn-element.utils";
import {
	answerText,
	answerTextCorrect,
	answerTextWrong,
} from "@components/multiple-choice-gap-text/multiple-choice-gap-text.styles";
import {
	correctAnswerTitle,
	correctAnswerWrapper,
	wrongAnswerTitleClass,
} from "@screens/elearning-content-submission/parts/lesson-insights/lesson-insights.styles";
import { success100 } from "@themes/colors";
import { P2Span } from "@themes/font-tags";
import { CLOZE_TEXT_LEARN_ELEMENT_FRAGMENT } from "./cloze-text-learn-element.graphql";
import {
	clozeTextLearnElementAnswerList,
	clozeTextLearnElementAnswerListItem,
	clozeTextLearnElementContentWrapper,
	clozeTextLearnElementCorrectAnswerContainer,
	clozeTextLearnElementCorrectAnswerWrapper,
	clozeTextLearnElementIconWrapper,
	clozeTextLearnElementWrapper,
	gapTextWrapperClass,
} from "./cloze-text-learn-element.styles";
import { ClozeTextLearnElementProps, PartTypeInterface } from "./cloze-text-learn-element.types";

export const ClozeTextLearnElement = ({
	isResultWrong,
	checkedIndices,
	answerIds,
	learnElementFragmentRef,
}: ClozeTextLearnElementProps) => {
	const element = useFragment(CLOZE_TEXT_LEARN_ELEMENT_FRAGMENT, learnElementFragmentRef ?? null);
	const [isCorrectShown, setIsCorrectShown] = useState(false);
	const parts = useMemo(() => element?.parts || [], [element?.parts]);
	const shuffledAnswers = element?.shuffledAnswers || [];
	const clozeIndexMap = useMemo(() => {
		const map: Record<number, number> = {};
		parts.forEach((part, index) => {
			if (part.kind === "cloze") {
				map[index] = Object.keys(map).length;
			}
		});
		return map;
	}, [parts]);

	const handleAnswerClick = () => {
		if (isResultWrong) {
			setIsCorrectShown((prev) => !prev);
		}
	};

	const handleHideCorrectAnswers = () => setIsCorrectShown(false);

	const ClozePart = ({ index }: { index: number }) => {
		const clozeIndex = clozeIndexMap[index] || 0;
		const clozeAnswerId = checkedIndices?.[clozeIndex];
		const answer = shuffledAnswers.find((a) => a.id === clozeAnswerId);
		return (
			<div className={gapTextWrapperClass} onClick={handleAnswerClick}>
				<span
					className={`${answerText} ${
						isResultWrong ? answerTextWrong : answerTextCorrect
					}`}
				>
					{answer?.text}
				</span>
			</div>
		);
	};

	const renderPart = (part: PartTypeInterface, index: number) => {
		return match(part.kind)
			.with("text", () => (
				<P2Span key={`${part.text}_${index}`} onClick={handleAnswerClick}>
					{part.text}
				</P2Span>
			))
			.with("cloze", () => <ClozePart key={`${part.text}_${index}`} index={index} />)
			.exhaustive();
	};
	return (
		<>
			<div className={clozeTextLearnElementWrapper}>
				<h6 className={wrongAnswerTitleClass}>Deine Antwort</h6>
			</div>

			{stripHtmlFromTextParts(parts)?.map((part, index) => renderPart(part, index))}

			{isCorrectShown && (
				<div
					className={`${correctAnswerWrapper} ${clozeTextLearnElementCorrectAnswerWrapper}`}
					onClick={handleHideCorrectAnswers}
				>
					<div className={clozeTextLearnElementCorrectAnswerContainer}>
						<div className={clozeTextLearnElementIconWrapper}>
							<Icon
								icon={"arrowUp" as keyof IconMap}
								color={success100}
								sizeRem={0.875}
							/>
						</div>

						<div className={clozeTextLearnElementContentWrapper}>
							<h6 className={correctAnswerTitle}>Lösung</h6>
							<div className={clozeTextLearnElementAnswerList}>
								<div className={clozeTextLearnElementAnswerListItem}>
									<span className={`${answerText} ${answerTextCorrect}`}>
										•{" "}
										{shuffledAnswers
											?.filter((item) => answerIds?.includes(item.id))
											.map((item) => item.text)
											.join(", ")}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};
